import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { moderatorService, Report, ModeratorStats } from '../../services/moderatorService';
import {
  ShieldExclamationIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  FlagIcon,
  ChatBubbleLeftIcon,
  UserIcon,
  EnvelopeIcon,
  ArrowTrendingUpIcon,
} from '@heroicons/react/24/outline';

const ModeratorDashboard: React.FC = () => {
  const [stats, setStats] = useState<ModeratorStats | null>(null);
  const [reports, setReports] = useState<Report[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('PENDING');
  const [selectedReport, setSelectedReport] = useState<Report | null>(null);
  const [actionModalOpen, setActionModalOpen] = useState(false);
  const [actionType, setActionType] = useState<'DISMISS' | 'REMOVE_CONTENT' | 'WARN_USER' | 'SUSPEND_USER' | 'ESCALATE'>('DISMISS');
  const [actionReason, setActionReason] = useState('');
  const [actionNotes, setActionNotes] = useState('');
  const [warningSeverity, setWarningSeverity] = useState<'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL'>('MEDIUM');
  const [suspendUntil, setSuspendUntil] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadData();
  }, [statusFilter]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [statsData, reportsData] = await Promise.all([
        moderatorService.getStats(),
        moderatorService.getReports({ status: statusFilter, limit: 50 }),
      ]);
      setStats(statsData);
      setReports(reportsData.reports);
    } catch (error) {
      console.error('Failed to load moderator data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReviewReport = async () => {
    if (!selectedReport) return;

    try {
      setSubmitting(true);
      const data: any = {
        action: actionType,
        reason: actionReason,
        notes: actionNotes,
      };

      if (actionType === 'WARN_USER') {
        data.warningSeverity = warningSeverity;
      }

      if (actionType === 'SUSPEND_USER' && suspendUntil) {
        data.suspendUntil = suspendUntil;
      }

      await moderatorService.reviewReport(selectedReport.id, data);
      
      // Refresh data
      await loadData();
      
      // Close modal and reset form
      setActionModalOpen(false);
      setSelectedReport(null);
      setActionReason('');
      setActionNotes('');
      setSuspendUntil('');
    } catch (error) {
      console.error('Failed to review report:', error);
      alert('Failed to submit action. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const openActionModal = (report: Report) => {
    setSelectedReport(report);
    setActionModalOpen(true);
    setActionType('DISMISS');
    setActionReason('');
    setActionNotes('');
  };

  const getTargetTypeIcon = (type: string) => {
    switch (type) {
      case 'POST':
        return <FlagIcon className="h-5 w-5" />;
      case 'COMMENT':
        return <ChatBubbleLeftIcon className="h-5 w-5" />;
      case 'USER':
        return <UserIcon className="h-5 w-5" />;
      case 'MESSAGE':
        return <EnvelopeIcon className="h-5 w-5" />;
      default:
        return <FlagIcon className="h-5 w-5" />;
    }
  };

  const getStatusBadge = (status: string) => {
    const statusColors = {
      PENDING: 'bg-yellow-100 text-yellow-800',
      REVIEWING: 'bg-blue-100 text-blue-800',
      RESOLVED: 'bg-green-100 text-green-800',
      DISMISSED: 'bg-gray-100 text-gray-800',
    };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColors[status as keyof typeof statusColors]}`}>
        {status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <ArrowPathIcon className="h-8 w-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Moderator Dashboard</h1>
        <p className="mt-2 text-sm text-gray-600">Review reports and manage content moderation</p>
      </div>

      {/* Stats Grid */}
      {stats && (
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0 }}
            className="relative overflow-hidden rounded-xl shadow-lg"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-orange-500 to-amber-600 opacity-90"></div>
            <div className="relative backdrop-blur-sm bg-white/20 p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 bg-white/30 rounded-lg backdrop-blur-sm">
                  <FlagIcon className="w-6 h-6 text-white" />
                </div>
                {stats.openReports > 0 && (
                  <span className="bg-red-500 text-white px-2 py-1 rounded-full text-xs font-bold shadow-lg">
                    {stats.openReports}
                  </span>
                )}
              </div>
              <h3 className="text-3xl font-bold text-white">{stats.openReports}</h3>
              <p className="text-white/90 text-sm mt-1 font-medium">Open Reports</p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="relative overflow-hidden rounded-xl shadow-lg"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-green-500 to-emerald-600 opacity-90"></div>
            <div className="relative backdrop-blur-sm bg-white/20 p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 bg-white/30 rounded-lg backdrop-blur-sm">
                  <CheckCircleIcon className="w-6 h-6 text-white" />
                </div>
                <ArrowTrendingUpIcon className="w-5 h-5 text-white/80" />
              </div>
              <h3 className="text-3xl font-bold text-white">{stats.resolvedToday}</h3>
              <p className="text-white/90 text-sm mt-1 font-medium">Resolved Today</p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="relative overflow-hidden rounded-xl shadow-lg"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-indigo-600 opacity-90"></div>
            <div className="relative backdrop-blur-sm bg-white/20 p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 bg-white/30 rounded-lg backdrop-blur-sm">
                  <ShieldExclamationIcon className="w-6 h-6 text-white" />
                </div>
              </div>
              <h3 className="text-3xl font-bold text-white">{stats.totalActions}</h3>
              <p className="text-white/90 text-sm mt-1 font-medium">Total Actions</p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="relative overflow-hidden rounded-xl shadow-lg"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-red-500 to-pink-600 opacity-90"></div>
            <div className="relative backdrop-blur-sm bg-white/20 p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 bg-white/30 rounded-lg backdrop-blur-sm">
                  <ExclamationTriangleIcon className="w-6 h-6 text-white" />
                </div>
                {stats.activeWarnings > 0 && (
                  <span className="bg-yellow-500 text-white px-2 py-1 rounded-full text-xs font-bold shadow-lg">
                    Active
                  </span>
                )}
              </div>
              <h3 className="text-3xl font-bold text-white">{stats.activeWarnings}</h3>
              <p className="text-white/90 text-sm mt-1 font-medium">Active Warnings</p>
            </div>
          </motion.div>
        </div>
      )}

      {/* Filter Tabs */}
      <div className="bg-white shadow rounded-lg mb-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 px-6" aria-label="Tabs">
            {['PENDING', 'REVIEWING', 'RESOLVED', 'DISMISSED'].map((status) => (
              <button
                key={status}
                onClick={() => setStatusFilter(status)}
                className={`${
                  statusFilter === status
                    ? 'border-orange-500 text-orange-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
              >
                {status}
              </button>
            ))}
          </nav>
        </div>

        {/* Reports List */}
        <div className="divide-y divide-gray-200">
          {reports.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <FlagIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <p>No reports found with status: {statusFilter}</p>
            </div>
          ) : (
            reports.map((report, index) => (
              <motion.div 
                key={report.id} 
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                className="p-6 hover:bg-gradient-to-r hover:from-orange-50 hover:to-amber-50 transition-all duration-300"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-4 flex-1">
                    <div className="flex-shrink-0 text-gray-400">
                      {getTargetTypeIcon(report.targetType)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2 mb-2">
                        <span className="text-sm font-medium text-gray-900">{report.targetType}</span>
                        {getStatusBadge(report.status)}
                      </div>
                      <p className="text-sm font-medium text-gray-700 mb-1">Reason: {report.reason}</p>
                      {report.description && (
                        <p className="text-sm text-gray-600 mb-2">{report.description}</p>
                      )}
                      <div className="flex items-center space-x-4 text-xs text-gray-500">
                        <span>Reported by: {report.reporter?.username || 'Unknown'}</span>
                        <span>•</span>
                        <span>{new Date(report.createdAt).toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                  <div className="ml-4">
                    {report.status === 'PENDING' && (
                      <button
                        onClick={() => openActionModal(report)}
                        className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
                      >
                        Review
                      </button>
                    )}
                  </div>
                </div>
              </motion.div>
            ))
          )}
        </div>
      </div>

      {/* Action Modal */}
      {actionModalOpen && selectedReport && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-medium text-gray-900">Review Report</h3>
                <button
                  onClick={() => setActionModalOpen(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <XCircleIcon className="h-6 w-6" />
                </button>
              </div>

              {/* Report Details */}
              <div className="bg-gray-50 p-4 rounded-lg mb-6">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium text-gray-700">Target Type:</span>
                    <span className="ml-2 text-gray-900">{selectedReport.targetType}</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Status:</span>
                    <span className="ml-2">{getStatusBadge(selectedReport.status)}</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Reporter:</span>
                    <span className="ml-2 text-gray-900">{selectedReport.reporter?.username}</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Reported:</span>
                    <span className="ml-2 text-gray-900">
                      {new Date(selectedReport.createdAt).toLocaleString()}
                    </span>
                  </div>
                </div>
                <div className="mt-3">
                  <span className="font-medium text-gray-700">Reason:</span>
                  <p className="mt-1 text-gray-900">{selectedReport.reason}</p>
                </div>
                {selectedReport.description && (
                  <div className="mt-3">
                    <span className="font-medium text-gray-700">Description:</span>
                    <p className="mt-1 text-gray-900">{selectedReport.description}</p>
                  </div>
                )}
              </div>

              {/* Action Form */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Action
                  </label>
                  <select
                    value={actionType}
                    onChange={(e) => setActionType(e.target.value as any)}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                  >
                    <option value="DISMISS">Dismiss Report</option>
                    <option value="REMOVE_CONTENT">Remove Content</option>
                    <option value="WARN_USER">Warn User</option>
                    <option value="SUSPEND_USER">Suspend User</option>
                    <option value="ESCALATE">Escalate to Admin</option>
                  </select>
                </div>

                {actionType === 'WARN_USER' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Warning Severity
                    </label>
                    <select
                      value={warningSeverity}
                      onChange={(e) => setWarningSeverity(e.target.value as any)}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                    >
                      <option value="LOW">Low</option>
                      <option value="MEDIUM">Medium</option>
                      <option value="HIGH">High</option>
                      <option value="CRITICAL">Critical</option>
                    </select>
                  </div>
                )}

                {actionType === 'SUSPEND_USER' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Suspend Until (optional)
                    </label>
                    <input
                      type="datetime-local"
                      value={suspendUntil}
                      onChange={(e) => setSuspendUntil(e.target.value)}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                    />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Reason
                  </label>
                  <input
                    type="text"
                    value={actionReason}
                    onChange={(e) => setActionReason(e.target.value)}
                    placeholder="Brief reason for this action"
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Notes (optional)
                  </label>
                  <textarea
                    value={actionNotes}
                    onChange={(e) => setActionNotes(e.target.value)}
                    rows={3}
                    placeholder="Additional notes or context"
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="mt-6 flex space-x-3">
                <button
                  onClick={handleReviewReport}
                  disabled={submitting}
                  className="flex-1 inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50"
                >
                  {submitting ? 'Submitting...' : 'Submit Action'}
                </button>
                <button
                  onClick={() => setActionModalOpen(false)}
                  disabled={submitting}
                  className="flex-1 inline-flex justify-center items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ModeratorDashboard;
